import React, { useEffect, useRef, useState } from "react";
import "./EventCard.css";
import {
  SendIcon,
  ClockIcon,
  ShieldCheckIcon,
  CalendarIcon,
  VIPIcon,
} from "../../helper/icon";
import InviteTypeSelectorModal from "../../Modals/invieTypeSelector/InviteTypeSelectorModal";
import ShareLinkModal from "../../Modals/shareLinkModal/ShareLinkModal";
import { useAuth } from "../../context/auth";

const EventCard = ({
  title,
  type,
  date,
  time,
  location,
  image,
  attendees = [],
  invitations = [],
  onPress,
  onRSVP,
  onDecline,
  onAccept,
  showButtons = false,
  showRSVPButton = true,
  showAttendees = true,
  showExternalLink = true,
  isVIP = false,
  status,
  isUsher,
  checkedInCount,
  totalGuests,
  approvalStatus,
  event,
}) => {
  const { user } = useAuth({});
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [inviteSelectorVisible, setInviteSelectorVisible] = useState(false);
  const [inviteRole, setInviteRole] = useState("guest");
  const scrollViewRef = useRef(null);
  const typeScrollViewRef = useRef(null);
  const scrollOffsetRef = useRef(0);
  const typeScrollOffsetRef = useRef(0);

  const defaultAvatar = "https://via.placeholder.com/25";

  const isHost = user?.activeRole === "host";

  const getDisplayDateTime = () => {
    if (!event) return { displayDate: date, displayTime: time, location };

    if (event.dateOption === "multiple" && event.multipleDates?.[0]) {
      const sortedDates = [...event.multipleDates].sort((a, b) => {
        const dateA = new Date(a.date?.$date || a.date || a.startDate);
        const dateB = new Date(b.date?.$date || b.date || b.startDate);
        return dateA - dateB;
      });
      const firstDate = sortedDates[0];

      let formattedDate = "N/A";
      if (firstDate.date) {
        const dateStr =
          typeof firstDate.date === "string"
            ? firstDate.date
            : firstDate.date.$date || firstDate.date;

        const dateObj = new Date(dateStr);
        formattedDate = `${dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })}`;
      }

      return {
        displayDate: formattedDate,
        displayTime: firstDate.time || "N/A",
        location: firstDate.landmark || firstDate.location || location,
      };
    }

    if (event.dateOption === "startEnd") {
      let formattedDate = date;
      if (event.startDate) {
        const dateStr =
          typeof event.startDate === "string"
            ? event.startDate
            : event.startDate.$date || event.startDate;

        const dateObj = new Date(dateStr);
        formattedDate = dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        });
      }

      return {
        displayDate: formattedDate,
        displayTime: event.startTime || time,
        location: event.eventLandmark || event.eventLocation || location,
      };
    }

    let formattedDate = date;
    if (event.eventDate) {
      const dateStr =
        typeof event.eventDate === "string"
          ? event.eventDate
          : event.eventDate.$date || event.eventDate;

      const dateObj = new Date(dateStr);
      formattedDate = dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    }

    return {
      displayDate: formattedDate,
      displayTime: event.eventTime || time,
      location:
        event.eventLandmark ||
        event.eventLocation ||
        event.eventLandmark ||
        location,
    };
  };

  const {
    displayDate,
    displayTime,
    location: displayLocation,
  } = getDisplayDateTime();

  const attendeeImages =
    invitations
      ?.filter(
        (inv) =>
          inv.guestId && (inv.guestId.userPhoto?.uri || inv.guestId.userPhoto)
      )
      .map((inv) =>
        inv.guestId.userPhoto?.uri
          ? inv.guestId.userPhoto.uri
          : inv.guestId.userPhoto
      ) || [];

  const displayedAttendees = attendeeImages.slice(0, 5);
  const remainingAttendees = Math.max(
    0,
    attendeeImages.length - displayedAttendees.length
  );

  const handleShareButtonPress = () => {
    setInviteSelectorVisible(true);
  };
  const handleScroll = (event) => {
    scrollOffsetRef.current = event.target.scrollLeft;
  };

  const handleTypeScroll = (event) => {
    typeScrollOffsetRef.current = event.target.scrollLeft;
  };

  useEffect(() => {
    const scrollRef = scrollViewRef.current;
    let isScrollingLeft = true;
    const scrollSpeed = 2;
    let scrollWidth = 0;
    let contentWidth = 0;

    setTimeout(() => {
      if (scrollRef) {
        scrollWidth = scrollRef.scrollWidth;
        contentWidth = scrollRef.clientWidth;
      }
    }, 100);

    const intervalId = setInterval(() => {
      if (scrollRef) {
        if (
          isScrollingLeft &&
          scrollOffsetRef.current + contentWidth >= scrollWidth
        ) {
          isScrollingLeft = false;
        } else if (!isScrollingLeft && scrollOffsetRef.current <= 0) {
          isScrollingLeft = true;
        }

        const newOffset = isScrollingLeft
          ? scrollOffsetRef.current + scrollSpeed
          : scrollOffsetRef.current - scrollSpeed;

        scrollRef.scrollTo({ left: newOffset, behavior: "smooth" });
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const typeScrollRef = typeScrollViewRef.current;
    let isScrollingLeft = true;
    const scrollSpeed = 1;
    let scrollWidth = 0;
    let contentWidth = 0;

    setTimeout(() => {
      if (typeScrollRef) {
        scrollWidth = typeScrollRef.scrollWidth;
        contentWidth = typeScrollRef.clientWidth;
      }
    }, 100);

    const intervalId = setInterval(() => {
      if (typeScrollRef) {
        if (
          isScrollingLeft &&
          typeScrollOffsetRef.current + contentWidth >= scrollWidth
        ) {
          isScrollingLeft = false;
        } else if (!isScrollingLeft && typeScrollOffsetRef.current <= 0) {
          isScrollingLeft = true;
        }

        const newOffset = isScrollingLeft
          ? typeScrollOffsetRef.current + scrollSpeed
          : typeScrollOffsetRef.current - scrollSpeed;

        typeScrollRef.scrollTo({ left: newOffset, behavior: "smooth" });
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, []);

  const getStatusStyle = () => {
    switch (status) {
      case "Accepted":
        return { backgroundColor: "#E7F5EC", color: "#0C7931" };
      case "Declined":
        return { backgroundColor: "#FFEBEB", color: "#FF5151" };
      default:
        return {};
    }
  };

  const getVisibilityBadge = () => {
    if (!event?.visibility) return null;

    const isPublic = event.visibility === "public";

    return (
      <div
        className="event-card-visibility-badge"
        style={{
          backgroundColor: isPublic ? "#E7F5EC" : "#FFF4E6",
          border: `1px solid ${isPublic ? "#0C7931" : "#FF9800"}`,
        }}
      >
        <span style={{ fontSize: "14px", marginRight: "4px" }}>
          {isPublic ? "🌐" : "🔒"}
        </span>
        <span
          style={{
            color: isPublic ? "#0C7931" : "#FF9800",
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {isPublic ? "Public" : "Private"}
        </span>
      </div>
    );
  };

  const formatEventDate = (dateString) => {
    return dateString;
  };

  return (
    <div
      className="event-card-container"
      onClick={onPress}
      style={{ cursor: onPress ? "pointer" : "default" }}
    >
      <div className="event-card-card">
        <div className="event-card-image">
          <img
            src={image || "https://via.placeholder.com/100"}
            alt={title}
            onError={(e) => (e.target.src = "https://via.placeholder.com/100")}
          />
        </div>

        <div className="event-card-details">
          <div className="event-card-header">
            <div>
              <h3 className="event-card-title">{title}</h3>
              <div
                className="event-card-scroll-container"
                ref={typeScrollViewRef}
                onScroll={handleTypeScroll}
                style={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                <div style={{ display: "inline-flex", alignItems: "center" }}>
                  {status && status !== "pending" && (
                    <span
                      className="event-card-status"
                      style={{
                        ...getStatusStyle(),
                        padding: "5px 10px",
                        borderRadius: "10px",
                        marginRight: "8px",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {status}
                    </span>
                  )}

                  <span className="event-card-type">{type}</span>

                  {getVisibilityBadge()}

                  {showRSVPButton && isVIP && (
                    <div
                      className="event-card-rsvp-button"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        backgroundColor: "#FF99001F",
                        border: "1px solid #FF9900",
                        borderRadius: "4px",
                        padding: "5px 10px",
                        marginLeft: "8px",
                      }}
                    >
                      <VIPIcon />
                      <span
                        style={{
                          color: "#FF9800",
                          marginLeft: "4px",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        VIP
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showAttendees && (
          <div className="event-card-attendees">
            <div className="event-card-attendee-avatars">
              {displayedAttendees.map((attendee, index) => (
                <img
                  key={index}
                  src={attendee || defaultAvatar}
                  alt="attendee"
                  className="event-card-attendee-avatar"
                  onError={(e) => (e.target.src = defaultAvatar)}
                />
              ))}

              {remainingAttendees > 0 && (
                <div className="event-card-attendee-count">
                  <span>+{remainingAttendees}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className="event-card-event-scroll-container"
        ref={scrollViewRef}
        onScroll={handleScroll}
      >
        <div className="event-card-info" style={{ display: "inline-flex" }}>
          {displayTime && (
            <div className="event-card-time">
              <span className="event-card-material-icons">
                <ClockIcon />
              </span>
              <span>{displayTime}</span>
            </div>
          )}

          {displayDate && (
            <>
              <div className="event-card-divider"></div>
              <div className="event-card-time">
                <span className="event-card-material-icons">
                  <CalendarIcon />
                </span>
                <span>{formatEventDate(displayDate)}</span>
              </div>
            </>
          )}

          {displayLocation && (
            <>
              <div className="event-card-divider"></div>
              <div className="event-card-location">
                <span className="event-card-material-icons">
                  <ShieldCheckIcon />
                </span>
                <span>{displayLocation}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="event-card-right">
        {isHost ? (
          approvalStatus === "approved" ? (
            <button
              className="event-card-share-button"
              onClick={(e) => {
                e.stopPropagation();
                handleShareButtonPress();
              }}
            >
              <SendIcon />
              <span>Share Invite</span>
            </button>
          ) : (
            <div className="event-card-status-badge">
              {approvalStatus === "pending" ? (
                <ClockIcon />
              ) : (
                <ShieldCheckIcon />
              )}
              <span className="event-card-status-text">
                {approvalStatus === "pending" ? "Pending" : "Declined"}
              </span>
            </div>
          )
        ) : null}
      </div>

      {showButtons && (!status || status === "pending") && (
        <div
          className="event-card-buttons-container"
          style={{
            display: "flex",
            padding: "10px 15px",
            justifyContent: "space-between",
          }}
        >
          <button
            className="event-card-decline-button"
            onClick={onDecline}
            style={{
              flex: 1,
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #DDDDDD",
              padding: "12px",
              marginRight: "6px",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#333333",
              }}
            >
              Decline
            </span>
          </button>
          <button
            className="event-card-accept-button"
            onClick={onAccept}
            style={{
              flex: 1,
              backgroundColor: "#006216",
              borderRadius: "8px",
              padding: "12px",
              marginLeft: "6px",
              cursor: "pointer",
              border: "none",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#FFFFFF",
              }}
            >
              Accept
            </span>
          </button>
        </div>
      )}

      <InviteTypeSelectorModal
        visible={inviteSelectorVisible}
        onClose={() => setInviteSelectorVisible(false)}
        onSelect={(role) => {
          setInviteRole(role);
          setShareModalVisible(true);
        }}
      />

      <ShareLinkModal
        visible={shareModalVisible}
        onClose={() => setShareModalVisible(false)}
        eventData={event}
        inviteRole={inviteRole}
      />
    </div>
  );
};

export default EventCard;
